// Data and arithmetic for the Quotation form.
//
// The GST POLICY LIVES ON THE SERVER. This file never decides whether a supply
// is intra- or inter-state: it asks GET /api/sales-ops/orders/bill-to/:contactId
// and applies whatever that says. Re-implementing the state table and the GSTIN
// checksum here would let a quotation be priced with one tax treatment on
// screen and saved with another — the arithmetic below is only the split of a
// rate the server has already decided the shape of.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';
import type { ProductHierarchyItem } from '@/features/catalog/hooks/useCatalogApi';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BillToAddress {
  companyName?: string;
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  gstin?: string;
}

export interface BillTo {
  customerName: string;
  crmContactId: string | null;
  customerGstin: string;
  /** From the contact (src/shared/sales/bill-to.js); printed on the document. */
  customerPhone?: string;
  customerEmail?: string;
  billingAddress: BillToAddress;
  shippingAddress?: BillToAddress;
  currency?: string;
  isInternational?: boolean;
  placeOfSupplyState: string;
  placeOfSupplyCode: string;
}

export interface BillToPreview {
  billTo: BillTo;
  placeOfSupply: { code: string; state: string; resolved: boolean; source: string };
  sellerStateCode: string;
  buyerStateCode: string;
  assumedPlaceOfSupply: boolean;
  intraState: boolean;
  /** Buyer outside India — no Indian place of supply and no GST. */
  isExport?: boolean;
  missingGstin: boolean;
  missingAddress: boolean;
}

export interface CatalogProduct {
  _id: string;
  name: string;
  sku?: string;
  // Matches the product schema's own field name (src/db-catalog.js) — the
  // list endpoint (/api/catalog/products) returns the field as `hsnSac`, not
  // `hsnCode`, and reading the wrong key here is why the HSN column used to
  // come back blank every time a product was picked.
  hsnSac?: string;
  sellingPrice?: number;
  mrp?: number;
  unitId?: { name?: string; shortName?: string };
  taxProfileId?: { name?: string; cgstRate?: number; sgstRate?: number; igstRate?: number };
  packagingType?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
}

export interface QuotationLine {
  incomeAccountId?: string | null;
  /** Client-side row key. Never sent. */
  key: string;
  productId: string | null;
  description: string;
  hsnSac: string;
  quantity: number;
  unitPrice: number;
  /** Original catalog INR price before currency conversion. Preserves accuracy on rate changes. */
  baseInrPrice?: number;
  unit: string;
  packSize: string;
  qtyPerPack: number;
  totalPackSize: string;
  volumeLiters: number;
  /** Discount can be entered as a percentage or a direct rupee amount. */
  discountType: 'percent' | 'fixed';
  discountPercent: number;
  discountAmount: number;
  /**
   * Per-line customisation note — how the customer wants THIS line made
   * ("red colour", "customer label", a pack spec). Prints under the product
   * name on the quotation, proforma, invoice and challan, and shows on the
   * Dispatch picking sheet, so it must survive the save.
   */
  notes: string;
  /** TOTAL GST percentage for the line, e.g. 18. Split below by supply type. */
  gstRate: number;
}

export function emptyLine(): QuotationLine {
  return {
    key: Math.random().toString(36).slice(2),
    productId: null,
    description: '',
    hsnSac: '',
    quantity: 1,
    unitPrice: 0,
    unit: 'LTR',
    packSize: '',
    qtyPerPack: 0,
    totalPackSize: '',
    volumeLiters: 0,
    discountType: 'percent',
    discountPercent: 0,
    discountAmount: 0,
    notes: '',
    gstRate: 18,
  };
}

/**
 * The GST percentage a catalog product carries.
 *
 * A tax profile stores the split (cgst 9 + sgst 9) AND the combined igst 18 for
 * the same 18% tax. Summing all three would give 36%, so the intra-state pair
 * is preferred and igst used only when that pair is absent.
 */
export function gstRateOf(p: CatalogProduct): number {
  const t = p.taxProfileId;
  if (!t) return 18;
  const pair = (Number(t.cgstRate) || 0) + (Number(t.sgstRate) || 0);
  if (pair > 0) return pair;
  return Number(t.igstRate) || 18;
}

/**
 * A line built from the RBM Product Selector (Division → Group → Grade → Pack
 * Size), the same picker the pipeline record form uses.
 *
 * Its items carry the split cgst/sgst pair AND igst for one tax, exactly like a
 * tax profile, so the same "prefer the pair, sum it once" rule applies — adding
 * all three would charge 36% for an 18% product.
 */
export function lineFromHierarchyItem(
  item: ProductHierarchyItem,
  quantity: number,
  customRate?: number,
): QuotationLine {
  const pair = (Number(item.cgstRate) || 0) + (Number(item.sgstRate) || 0);
  const rawPrice = customRate !== undefined ? customRate : (item.sellingPrice || item.mrp || 0);
  return {
    ...emptyLine(),
    productId: item._id,
    description: item.name,
    hsnSac: item.hsnSac || '',
    quantity: quantity > 0 ? quantity : 1,
    unitPrice: rawPrice,
    baseInrPrice: rawPrice,
    unit: abbreviatePackaging(derivePackagingType(item)) || item.unitName || 'LTR',
    packSize: item.packSize || '',
    qtyPerPack: Number(item.qtyPerPack) || 0,
    totalPackSize: item.totalPackSize || '',
    volumeLiters: Number(item.volumeLiters) || 0,
    gstRate: pair > 0 ? pair : (Number(item.igstRate) || 18),
  };
}

export function lineFromProduct(p: CatalogProduct): QuotationLine {
  const rawPrice = Number(p.sellingPrice) || Number(p.mrp) || 0;
  return {
    ...emptyLine(),
    productId: p._id,
    description: p.name,
    hsnSac: p.hsnSac || '',
    unitPrice: rawPrice,
    baseInrPrice: rawPrice,
    unit: abbreviatePackaging(derivePackagingType(p)) || p.unitId?.shortName || p.unitId?.name || 'LTR',
    packSize: p.packSize || '',
    qtyPerPack: Number(p.qtyPerPack) || 0,
    totalPackSize: p.totalPackSize || '',
    volumeLiters: Number(p.volumeLiters) || 0,
    gstRate: gstRateOf(p),
  };
}

// ── Queries ──────────────────────────────────────────────────────────────────

/**
 * Resolved bill-to block and supply type for a CRM contact.
 *
 * THE SHAPE CHECK IS NOT DEFENSIVE PADDING — it catches a specific trap in this
 * server. `app.use('/api', routeGuard)` authenticates every /api request, but an
 * UNMATCHED /api path then falls through to the SPA catch-all
 * (`app.get('*')` → sendFile(SPA_INDEX)) and comes back as **200 with
 * index.html**, not as a 404. Axios resolves that happily, so `data` is an HTML
 * string and every reader downstream is handed something shaped nothing like
 * what its type promised.
 *
 * That is what a backend running code older than the route looks like from the
 * browser, and it crashed this page the first time it was used. Rejecting a
 * payload that is not the expected object turns an invisible corruption into an
 * ordinary query error the UI already knows how to show.
 */
export function useBillToPreview(contactId: string | null | undefined) {
  return useQuery({
    queryKey: ['sales', 'bill-to', contactId],
    queryFn: async (): Promise<BillToPreview | null> => {
      if (!contactId) return null;
      const { data } = await api.get<BillToPreview>(`/api/sales-ops/orders/bill-to/${contactId}`);
      if (!data || typeof data !== 'object' || !data.billTo) {
        throw new Error(
          'The billing-details endpoint did not answer with billing details. If the server was '
          + 'updated recently it may need restarting.',
        );
      }
      return data;
    },
    enabled: !!contactId,
    // Retrying a structurally wrong answer just repeats it.
    retry: false,
  });
}

export function useCatalogProducts(search: string) {
  const debouncedSearch = useDebounce(search.trim(), 300);
  return useQuery({
    queryKey: ['catalog', 'products', 'quotation', debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50', isActive: 'true' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const { data } = await api.get<{ data: CatalogProduct[] }>(`/api/catalog/products?${params}`);
      return data.data ?? [];
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export interface TermPreset {
  _id: string;
  title: string;
  body: string;
  isDefault?: boolean;
}

/** Quotation Terms master (Master Data → Finance & Tax → Quotation Terms). */
export function useQuotationTerms() {
  return useQuery({
    queryKey: ['masters', 'quotation-terms'],
    queryFn: async () => {
      const { data } = await api.get<{ items?: TermPreset[] }>('/api/masters/special/quotation-terms?isActive=true');
      return data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
    // The terms library is a convenience: a quotation is perfectly valid with
    // hand-typed terms, so a missing master must not break the form.
    retry: false,
  });
}

// ── Arithmetic ───────────────────────────────────────────────────────────────

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export interface LineTotals {
  taxable: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
}

export interface QuotationTotals {
  lines: LineTotals[];
  subTotal: number;
  discountTotal: number;
  shippingCharge: number;
  /** GST on the freight, already inside cgstTotal/sgstTotal/igstTotal/taxTotal. */
  freightTax: number;
  cgstTotal: number;
  sgstTotal: number;
  igstTotal: number;
  taxTotal: number;
  roundOff?: number;
  grandTotal: number;
}

/**
 * Per-line GST, split according to the supply type the server resolved.
 *
 * Each line carries its own rate, so a document mixing 18% lubricants with a 5%
 * line is handled correctly — which the single flat CGST/SGST pair the Studio
 * applies to every row cannot express.
 */
export function computeQuotationTotals(
  lines: QuotationLine[],
  intraState: boolean,
  shippingCharge: number = 0,
  isInternational: boolean = false,
  roundOff?: number,
  /** SEZ / LUT: every line at this GST % instead of its own (0.1). */
  gstOverride?: number | null,
): QuotationTotals {
  const out: LineTotals[] = [];
  let subTotal = 0, discountTotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;

  for (const l of lines) {
    const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
    const discount = round2(l.discountType === 'fixed'
      ? Math.min(gross, Math.max(0, Number(l.discountAmount) || 0))
      : gross * ((Number(l.discountPercent) || 0) / 100));
    const taxable = round2(gross - discount);
    const rate = isInternational ? 0 : gstOverride != null ? gstOverride : (Number(l.gstRate) || 0);

    const cgstRate = isInternational ? 0 : (intraState ? rate / 2 : 0);
    const sgstRate = isInternational ? 0 : (intraState ? rate / 2 : 0);
    const igstRate = isInternational ? 0 : (intraState ? 0 : rate);

    const cgst = round2(taxable * (cgstRate / 100));
    const sgst = round2(taxable * (sgstRate / 100));
    const igst = round2(taxable * (igstRate / 100));

    subTotal += taxable;
    discountTotal += discount;
    cgstTotal += cgst;
    sgstTotal += sgst;
    igstTotal += igst;

    out.push({ taxable, cgstRate, sgstRate, igstRate, cgst, sgst, igst, total: round2(taxable + cgst + sgst + igst) });
  }

  subTotal = round2(subTotal);
  const ship = Math.max(0, Number(shippingCharge) || 0);
  // Freight is part of the taxable value: taxed at the lines' own rate and
  // split the way they are (0 on an export) — same rule as the server.
  if (ship > 0 && subTotal > 0) {
    const share = (t: number) => round2(ship * t / subTotal);
    const fc = share(cgstTotal), fs2 = share(sgstTotal), fi = share(igstTotal);
    cgstTotal += fc; sgstTotal += fs2; igstTotal += fi;
  }
  const lineTax = round2(out.reduce((sum, l) => sum + l.cgst + l.sgst + l.igst, 0));
  const taxTotal = round2(cgstTotal + sgstTotal + igstTotal);
  const preRound = round2(subTotal + taxTotal + ship);
  const autoRound = isInternational ? 0 : round2(Math.round(preRound) - preRound);
  const resolvedRoundOff = roundOff !== undefined && roundOff !== null && !isNaN(Number(roundOff))
    ? round2(Number(roundOff))
    : (isInternational ? 0 : (Math.abs(autoRound) <= 1 ? autoRound : 0));
  return {
    lines: out,
    subTotal,
    discountTotal: round2(discountTotal),
    shippingCharge: ship,
    freightTax: round2(taxTotal - lineTax),
    cgstTotal: round2(cgstTotal),
    sgstTotal: round2(sgstTotal),
    igstTotal: round2(igstTotal),
    taxTotal,
    roundOff: resolvedRoundOff,
    grandTotal: round2(preRound + resolvedRoundOff),
  };
}

export function useQuotationTotals(
  lines: QuotationLine[],
  intraState: boolean,
  shippingCharge: number = 0,
  isInternational: boolean = false,
  roundOff?: number,
  gstOverride?: number | null,
) {
  return useMemo(
    () => computeQuotationTotals(lines, intraState, shippingCharge, isInternational, roundOff, gstOverride),
    [lines, intraState, shippingCharge, isInternational, roundOff, gstOverride],
  );
}
